import {
  AloftArea,
  AloftAreaJSON,
  AloftPrice,
  AloftPriceJSON,
  Improvements,
  ManualAdjustmentKey,
  PropertyData,
  SixTierQuality,
  getFeatureQualityLabel,
  orUndefined,
  schAloftArea,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { Opts } from '@aloft/toolkit-sui';

import { isUndefined } from 'lodash';

import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { PropertyGridField } from '../types.ts';

export const getCompAdjustmentValue = (
  manualAdjustmentKey: ManualAdjustmentKey,
  compAdjustments?: CompAdjustments,
): AloftPrice => {
  if (!compAdjustments || !manualAdjustmentKey) return AloftPrice.zero;

  switch (manualAdjustmentKey) {
    case 'gla':
      return compAdjustments.mainDwelling.gla.adjustment ?? AloftPrice.zero;
    case 'poolSpa':
      return compAdjustments.pool.adjustment ?? AloftPrice.zero;
    case 'site':
      return compAdjustments.site.size.adjustment ?? AloftPrice.zero;
    case 'siteInfluence':
      return compAdjustments.siteInfluence?.adjustment ?? AloftPrice.zero;
    case 'siteView':
      return compAdjustments.siteView?.adjustment ?? AloftPrice.zero;
    case 'units':
      return compAdjustments.mainDwelling.units?.adjustment ?? AloftPrice.zero;
    default:
      return compAdjustments[manualAdjustmentKey].adjustment ?? AloftPrice.zero;
  }
};

export const getCompAdjustmentDisplayValue = (
  manualAdjustmentKey: ManualAdjustmentKey,
  compAdjustments?: CompAdjustments,
): AloftPrice => {
  const adjustmentValue = getCompAdjustmentValue(manualAdjustmentKey, compAdjustments);

  if (
    manualAdjustmentKey === 'functionalUtility' ||
    manualAdjustmentKey === 'externalObsolescence'
  ) {
    return adjustmentValue.mul(-1);
  }

  return adjustmentValue;
};

export const getAloftAreaJSONPropertyGridField = ({
  label = 'Area',
  ...rest
}: Partial<
  Omit<PropertyGridField<AloftAreaJSON>, 'fieldName' | 'toDisplayValue' | 'toEditValue' | 'toValue'>
> & {
  fieldName: PropertyGridField<AloftAreaJSON>['fieldName'];
}): PropertyGridField<AloftAreaJSON> => ({
  label,
  toDisplayValue: (value) => orUndefined(schAloftArea.parse)(value)?.getSiteAreaString(),
  toEditValue: (value) => orUndefined(schAloftArea.parse)(value)?.toSqFt().unitAmount.toString(),
  toValue: (value) => {
    if (isUndefined(value)) return undefined;

    const area = schAloftArea.safeParse(value);
    if (area.success) return area.data.toJSON();

    // Handle partial input gracefully (e.g., just "-" or empty string)
    const trimmedValue = value.trim();
    if (trimmedValue === '' || trimmedValue === '-') return undefined;

    try {
      const unit = value.includes('ac') ? 'acre' : 'sqft';
      return AloftArea.fromString(value, unit).toJSON();
    } catch {
      // If parsing fails, return undefined to allow partial input
      return undefined;
    }
  },
  ...rest,
});

export const getAloftPriceJSONPropertyGridField = ({
  label = 'Cost',
  ...rest
}: Partial<
  Omit<
    PropertyGridField<AloftPriceJSON>,
    'fieldName' | 'toDisplayValue' | 'toEditValue' | 'toValue'
  >
> & {
  fieldName: PropertyGridField<AloftPriceJSON>['fieldName'];
}): PropertyGridField<AloftPriceJSON> => ({
  label,
  toDisplayValue: (value) => orUndefined(schAloftPrice.parse)(value)?.toString(true),
  toEditValue: (value) => orUndefined(schAloftPrice.parse)(value)?.toDollarLike().toString(),
  toValue: (value) => {
    if (isUndefined(value)) return undefined;

    const price = schAloftPrice.safeParse(value);
    if (price.success) return price.data.toJSON();

    const trimmedValue = value.trim();
    if (trimmedValue === '' || trimmedValue === '$') return undefined;

    try {
      return AloftPrice.fromDollarLike(value).toJSON();
    } catch {
      return undefined;
    }
  },
  ...rest,
});

export const getNumberPropertyGridField = (
  field: Partial<Omit<PropertyGridField<number>, 'fieldName' | 'label' | 'toValue'>> & {
    fieldName: PropertyGridField<number>['fieldName'];
    label: PropertyGridField<number>['label'];
  },
): PropertyGridField<number> => ({
  toValue: (value) => orUndefined(parseFloat)(value),
  ...field,
});

export const getCraftsmanQualityPropertyGridField = ({
  fieldName,
  label = 'Craftsman Quality',
  ...rest
}: Partial<Omit<PropertyGridField<number>, 'fieldName' | 'options' | 'toValue'>> & {
  fieldName: PropertyGridField<number>['fieldName'];
}): PropertyGridField<number> => {
  const options: Opts<SixTierQuality> = [];

  if (['pool', 'spa'].some((feature) => fieldName.includes(feature))) {
    options.push({ label: 'Luxury', value: 1 });
    options.push({ label: 'Better', value: 2 });
    options.push({ label: 'Standard', value: 3 });
  } else if (['barn', 'shed', 'workshop'].some((feature) => fieldName.includes(feature))) {
    options.push({ label: 'Good', value: 1 });
    options.push({ label: 'Average', value: 2 });
    options.push({ label: 'Low', value: 3 });
  } else {
    options.push({ label: 'Luxury', value: 1 });
    options.push({ label: 'Semi-Luxury', value: 2 });
    options.push({ label: 'Best', value: 3 });
    options.push({ label: 'Good', value: 4 });
    options.push({ label: 'Average', value: 5 });
    options.push({ label: 'Minimum', value: 6 });
  }

  return getNumberPropertyGridField({ fieldName, label, options, ...rest });
};

export const getAreaAndQualityFeatureAdjustmentGridField = (
  type: 'barn' | 'shed' | 'workshop',
): PropertyGridField<PropertyData['editableData']['improvements']> => ({
  fieldName: 'editableData.improvements',
  label: type.charAt(0).toUpperCase() + type.slice(1),
  manualAdjustmentKey: type,
  toDisplayValue: (improvements?: Improvements) => {
    if (!improvements?.[type] || !improvements?.[type].hasFeature) return undefined;
    const { area, quality } = improvements[type];

    if (!area || !quality) return undefined;

    const areaString = AloftArea.schema.parse(area).getSiteAreaString('');
    const qualityString = getFeatureQualityLabel(type, quality, true);

    return `${areaString}/${qualityString}`;
  },
});

export const suffixField = <T extends PropertyGridField<string> | PropertyGridField<number>>(
  field: T,
  suffix: string,
  suffixPlural?: string,
): T => ({
  ...field,
  toDisplayValue: (value?: string | number) => {
    if (isUndefined(value)) return undefined;

    let suffixToUse = suffix;

    if (typeof value === 'number' && suffixPlural && Math.abs(value) !== 1) {
      suffixToUse = suffixPlural;
    }

    return [value, suffixToUse].join(' ');
  },
  toValue: (value) => {
    if (isUndefined(value)) return undefined;

    const parsed = value
      .toString()
      .replace(
        suffixPlural && value.toString().endsWith(suffixPlural)
          ? new RegExp(`${suffixPlural}$`)
          : new RegExp(`${suffix}$`),
        '',
      )
      .trim();

    const numberValue = parseFloat(parsed);

    return Number.isNaN(numberValue) ? parsed : numberValue;
  },
});
